import { EditIcon, TrashIcon } from 'lucide-react';
import type React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { FormDetailPane } from '@/components/common/FormDetailPane';
import { TextFormField } from '@/components/common/FormField';
import { Button } from '@/components/ui/button';
import { FieldGroup } from '@/components/ui/field';
import type { WorkingTimeModel } from '../data/databaseSchema';
import { workingTimeModelService } from '../services/workingTimeModelService';

interface WorkingTimeModelDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (model: WorkingTimeModel) => void;
  onEntrySaved?: () => void;
  model?: WorkingTimeModel | null;
  isEdit?: boolean;
  readOnly?: boolean;
  onEditRequest?: () => void;
  onDeleteRequest?: () => void;
  isDeleteDisabled?: boolean;
}

const WEEKDAYS = [
  { key: 'monday' as const, label: 'MON' },
  { key: 'tuesday' as const, label: 'TUE' },
  { key: 'wednesday' as const, label: 'WED' },
  { key: 'thursday' as const, label: 'THU' },
  { key: 'friday' as const, label: 'FRI' },
  { key: 'saturday' as const, label: 'SAT' },
  { key: 'sunday' as const, label: 'SUN' },
];

interface FormData {
  name: string;
  monday: number;
  tuesday: number;
  wednesday: number;
  thursday: number;
  friday: number;
  saturday: number;
  sunday: number;
}

export const WorkingTimeModelDrawer: React.FC<WorkingTimeModelDrawerProps> = ({
  isOpen,
  onClose,
  onSave,
  onEntrySaved,
  model,
  isEdit = false,
  readOnly = false,
  onEditRequest,
  onDeleteRequest,
  isDeleteDisabled = false,
}) => {
  const [formData, setFormData] = useState<FormData>({
    name: '',
    monday: 0,
    tuesday: 0,
    wednesday: 0,
    thursday: 0,
    friday: 0,
    saturday: 0,
    sunday: 0,
  });

  const totalHours = useMemo(() => {
    return (
      formData.monday +
      formData.tuesday +
      formData.wednesday +
      formData.thursday +
      formData.friday +
      formData.saturday +
      formData.sunday
    );
  }, [formData]);

  const fte = useMemo(() => totalHours / 40, [totalHours]);

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      if ((isEdit || readOnly) && model) {
        setFormData({
          name: model.name || '',
          monday: model.monday || 0,
          tuesday: model.tuesday || 0,
          wednesday: model.wednesday || 0,
          thursday: model.thursday || 0,
          friday: model.friday || 0,
          saturday: model.saturday || 0,
          sunday: model.sunday || 0,
        });
      } else {
        setFormData({
          name: '',
          monday: 0,
          tuesday: 0,
          wednesday: 0,
          thursday: 0,
          friday: 0,
          saturday: 0,
          sunday: 0,
        });
      }
      setErrors({});
      setSubmitError(null);
    }
  }, [isOpen, isEdit, readOnly, model]);

  const handleInputChange = (field: keyof FormData, value: number | string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name || formData.name.trim() === '') {
      newErrors.name = 'Name is required';
    }

    if (fte < 0) {
      newErrors.fte = 'FTE cannot be negative';
    }
    if (totalHours < 0) {
      newErrors.hours = 'Hours cannot be negative';
    }

    const days: Array<keyof FormData> = [
      'monday',
      'tuesday',
      'wednesday',
      'thursday',
      'friday',
      'saturday',
      'sunday',
    ];
    for (const day of days) {
      const value = formData[day];
      if (typeof value === 'number' && value < 0) {
        newErrors[day] = `${day.charAt(0).toUpperCase() + day.slice(1)} hours cannot be negative`;
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const apiData = {
        name: formData.name.trim(),
        monday: formData.monday,
        tuesday: formData.tuesday,
        wednesday: formData.wednesday,
        thursday: formData.thursday,
        friday: formData.friday,
        saturday: formData.saturday,
        sunday: formData.sunday,
      };

      const response =
        isEdit && model
          ? await workingTimeModelService.updateWorkingTimeModel(model.id, apiData)
          : await workingTimeModelService.createWorkingTimeModel(apiData);

      onSave(response);
      onEntrySaved?.();
      onClose();
    } catch (error) {
      setSubmitError(error instanceof Error ? error.message : 'An unexpected error occurred');
    } finally {
      setIsSubmitting(false);
    }
  };

  const fieldsDisabled = readOnly;

  return (
    <FormDetailPane
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      title={
        readOnly
          ? 'View Working Time Model'
          : isEdit
            ? 'Edit Working Time Model'
            : 'Add Working Time Model'
      }
      description={
        readOnly
          ? 'Working time model details.'
          : isEdit
            ? 'Update the working time model details below.'
            : 'Fill in the details for the new working time model.'
      }
      onSubmit={readOnly ? undefined : handleSubmit}
      submitLabel={isEdit ? 'Update Model' : 'Add Model'}
      isSubmitting={isSubmitting}
      submitError={submitError}
      showFooter={!readOnly}
    >
      {readOnly ? (
        <div className="flex flex-wrap justify-end gap-2 border-b border-border-default pb-4">
          {onEditRequest ? (
            <Button type="button" size="sm" onClick={onEditRequest}>
              <EditIcon data-icon="inline-start" />
              Edit
            </Button>
          ) : null}
          {onDeleteRequest ? (
            <Button
              type="button"
              size="sm"
              variant="destructive"
              disabled={isDeleteDisabled}
              onClick={onDeleteRequest}
            >
              <TrashIcon data-icon="inline-start" />
              Delete
            </Button>
          ) : null}
        </div>
      ) : null}
      <FieldGroup>
        <TextFormField
          label="Name"
          required
          value={formData.name}
          onChange={(v) => handleInputChange('name', v)}
          error={errors.name}
          placeholder="Enter model name"
          disabled={fieldsDisabled}
        />

        <div className="grid grid-cols-2 gap-4">
          <TextFormField
            label="Total Hours"
            type="number"
            value={String(totalHours)}
            onChange={() => {}}
            disabled
          />
          <TextFormField
            label="FTE"
            type="number"
            value={String(fte)}
            onChange={() => {}}
            disabled
          />
        </div>

        <div className="border-t border-border-default pt-4">
          <h4 className="mb-4 text-base font-semibold text-primary">Daily Hours</h4>
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-5">
            {WEEKDAYS.slice(0, 5).map(({ key, label }) => (
              <TextFormField
                key={key}
                label={label}
                type="number"
                value={String(formData[key])}
                onChange={(v) => handleInputChange(key, Number.parseFloat(v) || 0)}
                error={errors[key]}
                placeholder="0.00"
                disabled={fieldsDisabled}
              />
            ))}
            <div className="col-span-full hidden h-px bg-border-default sm:block" />
            {WEEKDAYS.slice(5).map(({ key, label }) => (
              <TextFormField
                key={key}
                label={label}
                type="number"
                value={String(formData[key])}
                onChange={(v) => handleInputChange(key, Number.parseFloat(v) || 0)}
                error={errors[key]}
                placeholder="0.00"
                disabled={fieldsDisabled}
              />
            ))}
          </div>
        </div>
      </FieldGroup>
    </FormDetailPane>
  );
};
